import clsx from 'clsx';
import { useRouter } from 'next/router';
import { ImageProps as NextImageProps } from 'next/image';
import Link, { LinkProps } from 'next/link';
import React, { useCallback, useMemo } from 'react';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { ProductEvent } from '~/lib/salesforce/types';
import { PromotionType } from '~/lib/data-contract/salesforce';
import Typography from '~/shared/components/Typography/Typography';
import { extractProductSplash, ProductSplash } from '~/shared/components/ProductSplash';
import { EventIdentifier, useEvents } from '~/shared/hooks/useEvents';
import { _transformProductDetailsToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { useProductLabels } from '~/shared/hooks/useProductLabels';
import {
    ComparisonItem,
    useHasComparisonItems,
    useIsInComparison,
} from '~/shared/productComparison';
import { useFrame } from '~/shared/utils';
import { normalizeHexColor } from '~/shared/utils/colors';
import { useTranslation } from '~/shared/utils/translation';
import { TProductDetails } from '../ProductCard/ProductCard.definition';
import {
    CompareToggle,
    extractProductAttributes,
    extractProductCampaign,
    extractProductPrices,
    ProductAttributes,
    ProductCampaign,
    ProductCardMedia,
    ProductPrices,
    WishlistButton,
} from './components';
import { Multibuy } from './components/MultibuyPrices/Multibuy';
import { getStockLabels } from './utils/getStockLabels';
import styles from './NewProductCard.module.scss';
import { replaceProductHyphensWithSlashes } from '~/lib/salesforce/utilities/formatters';

export type ProductCardVariants = 'opaque' | 'default';
export type ProductCardLayout = 'default' | 'condensed';

interface TrackingProps {
    trackingListId?: EventIdentifier;
    trackingListName?: EventIdentifier;
    raptorAlgorithm?: string;
    position: number;
}

interface ProductCardProps {
    className?: string;
    // TODO(IMC-1671): Align Product types to not need to "add" existing properties.
    product: TProductDetails & {
        c_ItemSoleRepresentation?: boolean;
    };
    variant?: ProductCardVariants;
    layout?: ProductCardLayout;
    isFavourite?: boolean;
    /**
     * Opt in to the product comparison entry point. Only the surfaces the comparison is
     * designed for (the product list and search result grids) pass this - elsewhere the card
     * shows no toggle and stays unhighlighted, even for a product already being compared.
     */
    showCompareToggle?: boolean;
    imagePriority?: boolean;
    imageLoading?: NextImageProps['loading'];
    onFavourite?(product: TProductDetails): void;
    onButtonClick?(
        listIdentifier: EventIdentifier,
        event: ProductEvent,
        product: TProductDetails,
        clickEvent: React.MouseEvent<HTMLAnchorElement>,
    ): void;
}

type Props = ProductCardProps & TrackingProps & Omit<LinkProps, 'href'>;

export const NewProductCard: React.FC<Props> = ({
    className,
    product,
    position,
    trackingListId = '',
    trackingListName,
    isFavourite,
    showCompareToggle,
    raptorAlgorithm,
    imagePriority,
    imageLoading,
    onFavourite,
    onButtonClick,
    variant = 'default',
    layout = 'default',
    onClick,
    ...rest
}) => {
    const { data: frame } = useFrame();
    const { translate } = useTranslation();
    // TODO(IMC-1672): Check if tracking can be moved to ancestors.
    const { productClickEvent } = useEvents(frame);
    const { isPlusMember } = useCustomer();
    const labels = useProductLabels(product);

    const { id, images, brand, name, longName, url } = product;

    const eventPayload = useMemo(
        () => _transformProductDetailsToDataLayer(product, 1, position, isPlusMember),
        [id, isPlusMember],
    );

    // TODO(IMC-1672): Check if tracking can be moved to ancestors.
    const handleCardClick = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            onClick?.(event);

            if (event.defaultPrevented) {
                return;
            }

            sessionStorage.setItem('itemClicked', JSON.stringify({ id }));

            productClickEvent(trackingListId, trackingListName, eventPayload, raptorAlgorithm);
            onButtonClick?.(
                trackingListId,
                { type: 'navigateto', payload: { url } },
                product,
                event,
            );
        },
        [
            eventPayload,
            id,
            onButtonClick,
            onClick,
            product,
            productClickEvent,
            raptorAlgorithm,
            trackingListId,
            trackingListName,
            url,
        ],
    );

    const productWithoutFewStockNotification = useMemo(
        () => ({
            ...product,
            notifications: product.notifications?.filter(({ type }) => type !== 'primary'),
        }),
        [product.id],
    );

    const stockLabels = useMemo(() => getStockLabels(product), [product.id]);

    const isComparing = useIsInComparison(id) && !!showCompareToggle;

    // Desktop only shows the compare toggle on hover, to keep the grid quiet - once a
    // comparison is actually in progress it stays visible everywhere without hovering, so
    // the customer can see (and keep adding to) their selection at a glance.
    const hasComparisonSelection = useHasComparisonItems();
    const { asPath } = useRouter();

    const comparisonItem = useMemo<ComparisonItem>(
        () => ({
            id,
            name: longName || name,
            url,
            brand,
            image: images?.[0]
                ? { src: images[0].src, alt: images[0].alt ?? longName ?? name }
                : undefined,
            // A card only ever renders on a listing, so that listing is where the tray sends
            // the customer back to for more products - read once they are on a product page
            // and no longer have a grid in front of them.
            categoryUrl: asPath,
        }),
        [id, asPath],
    );

    return (
        <Link
            className={clsx(styles.card, styles[variant], styles[layout], className)}
            {...rest}
            onClick={handleCardClick}
            href={url}
            aria-label={`${longName || name} – åbner produktsiden.`}
        >
            <div className={clsx(styles.media, isComparing && styles.isComparing)}>
                <ProductCardMedia
                    className={styles.images}
                    sources={images}
                    imagePriority={imagePriority}
                    imageLoading={imageLoading}
                    name={longName || name}
                />

                {!!labels?.length && (
                    <ul className={styles.labels}>
                        {labels.map((label) => (
                            <li
                                key={label.displayText}
                                className={styles.item}
                                style={{
                                    backgroundColor: normalizeHexColor(label.backgroundColor),
                                    color: normalizeHexColor(label.textColor),
                                }}
                            >
                                {label.displayText}
                            </li>
                        ))}
                    </ul>
                )}

                <ProductSplash className={styles.splash} {...extractProductSplash(product)} />

                {showCompareToggle && (
                    <CompareToggle
                        className={clsx(
                            styles.compareToggle,
                            hasComparisonSelection && styles.isForced,
                        )}
                        item={comparisonItem}
                    />
                )}
            </div>

            <div className={styles.content}>
                <div className={styles.titles}>
                    <p className={styles.brand} title={brand}>
                        {brand}
                    </p>

                    {longName && (
                        <p className={styles.name} title={longName}>
                            {replaceProductHyphensWithSlashes(longName)}
                        </p>
                    )}
                </div>

                <WishlistButton
                    className={styles.wishlistButton}
                    isActive={isFavourite}
                    onDebouncedClick={() => onFavourite?.(product)}
                />

                <ProductAttributes
                    className={styles.attributes}
                    {...extractProductAttributes(product)}
                />
                {/* TODO:IMC-1752 test renaming class if multibuy to be reverted if not fixed the issue */}
                {product.cust_promotionType?.toString() == PromotionType.MULTI_BUY ? (
                    <Multibuy className={styles.multibuy} {...extractProductPrices(product)} />
                ) : (
                    <ProductPrices
                        className={styles.prices}
                        isCondensed={layout === 'condensed'}
                        {...extractProductPrices(product)}
                    />
                )}

                <ProductCampaign
                    className={styles.campaignText}
                    {...extractProductCampaign(productWithoutFewStockNotification)}
                />

                {!!stockLabels.length && (
                    <ul className={styles.stockLabelList}>
                        {stockLabels.map(({ key, tone, isTextGrey }) => (
                            <li key={key} className={styles.stockLabelItem}>
                                <span className={clsx(styles.stockLabelDot, styles[tone])} />
                                <Typography
                                    as="span"
                                    variant="paragraph-XS"
                                    className={clsx(isTextGrey && styles.textGrey)}
                                >
                                    {translate(key)}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Link>
    );
};
